import { lazy, type ReactNode, Suspense, useId, useState } from 'react';
import buttons from '../../styles/buttons.module.css';
import { Icon } from '../Icon.tsx';
import { StatusMessage } from '../StatusMessage.tsx';
import styles from './fields.module.css';

// Accessible form primitives: every control has a visible label bound by id,
// an optional description, and an inline error announced through
// aria-describedby. Touch targets follow the 44px token.
//
// A text field can also carry a camera button (the `scan` prop) that reads
// its value off an equipment label. The camera panel and its decoder are a
// separate chunk, fetched the first time someone taps the button, so every
// form that only ever gets typed into costs nothing for it.

const ScanSheet = lazy(async () => {
  const module = await import('../../scan/ScanSheet.tsx');
  return { default: module.ScanSheet };
});

interface BaseProps {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

/** The ids of the hint and error paragraphs, for aria-describedby. */
function describedBy(id: string, hint: string | undefined, error: string | undefined) {
  const parts = [hint === undefined ? '' : `${id}-hint`, error === undefined ? '' : `${id}-error`];
  const present = parts.filter((part) => part !== '');
  return present.length === 0 ? undefined : present.join(' ');
}

interface FieldShellProps extends BaseProps {
  readonly id: string;
  readonly children: ReactNode;
}

/** Label above, hint under it, the control, then the error: every field's frame. */
function FieldShell({ id, label, hint, error, children }: FieldShellProps) {
  return (
    <div className={styles['field']}>
      <label className={styles['label']} htmlFor={id}>
        {label}
      </label>
      {hint === undefined ? null : (
        <p id={`${id}-hint`} className={styles['hint']}>
          {hint}
        </p>
      )}
      {children}
      {error === undefined ? null : (
        <p id={`${id}-error`} className={styles['error']} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextFieldProps extends BaseProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly inputMode?: 'text' | 'numeric' | 'decimal' | 'url' | 'email' | 'tel';
  readonly multiline?: boolean;
  readonly placeholder?: string | undefined;
  /** A `date` field holds 'YYYY-MM-DD' and opens the device's own picker. */
  readonly type?: 'text' | 'password' | 'date';
  /**
   * Suggestions offered through a `<datalist>` while typing, without
   * restricting what can be entered (ROADMAP B4's make field). A value the
   * list does not hold is still accepted, the way the map records what is
   * really installed.
   */
  readonly list?: readonly string[] | undefined;
  /**
   * A camera button beside the field that reads the value off the
   * equipment's own label (a serial, a MAC). What is scanned is shown for
   * confirmation and written in through `scan`, which is the field's chance
   * to put it into the shape it stores — so a MAC becomes colon-separated
   * pairs whichever way the label printed it. Left out, the field is a
   * plain text field and none of the scanning code is fetched.
   */
  readonly scan?: (scanned: string) => string;
}

export function TextField({
  label,
  value,
  onChange,
  error,
  hint,
  required = false,
  autoComplete = 'off',
  inputMode = 'text',
  multiline = false,
  placeholder,
  type = 'text',
  list,
  scan,
}: TextFieldProps) {
  const id = useId();
  const listId = `${id}-list`;
  // An empty suggestion list renders no datalist, so a field with nothing to
  // suggest is a plain input rather than one bound to an empty list.
  const hasList = list !== undefined && list.length > 0;
  const [scanning, setScanning] = useState(false);
  const shared = {
    id,
    className: styles['input'],
    value,
    required,
    autoComplete,
    'aria-invalid': error === undefined ? undefined : true,
    'aria-describedby': describedBy(id, hint, error),
  };
  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      {multiline ? (
        <textarea
          {...shared}
          className={`${styles['input'] ?? ''} ${styles['textarea'] ?? ''}`}
          rows={3}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : (
        <div className={styles['with-scan']}>
          <input
            {...shared}
            type={type}
            inputMode={inputMode}
            placeholder={placeholder}
            list={hasList ? listId : undefined}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
          {list !== undefined && list.length > 0 ? (
            <datalist id={listId}>
              {list.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          ) : null}
          {scan === undefined ? null : (
            <button
              className={`${buttons['secondary'] ?? ''} ${buttons['icon'] ?? ''}`}
              type="button"
              aria-label={`Scan ${label.toLowerCase()}`}
              onClick={() => {
                setScanning(true);
              }}
            >
              <Icon name="camera" />
            </button>
          )}
        </div>
      )}
      {scan === undefined || !scanning ? null : (
        <Suspense fallback={<StatusMessage>Loading the camera…</StatusMessage>}>
          <ScanSheet
            title={`Scan ${label.toLowerCase()}`}
            label={label}
            onUse={(scanned) => {
              onChange(scan(scanned));
              setScanning(false);
            }}
            onClose={() => {
              setScanning(false);
            }}
          />
        </Suspense>
      )}
    </FieldShell>
  );
}

interface NumberFieldProps extends BaseProps {
  /** Empty string means "not set". */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** With a range the field is a stepper: type the value or tap − and +. */
  readonly min?: number | undefined;
  readonly max?: number | undefined;
  /** Common values, offered as one-tap picks above the stepper. */
  readonly presets?: readonly number[] | undefined;
}

export function NumberField({
  label,
  value,
  onChange,
  error,
  hint,
  min,
  max,
  presets = [],
}: NumberFieldProps) {
  const id = useId();
  if (min === undefined || max === undefined) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={onChange}
        error={error}
        hint={hint}
        inputMode="decimal"
      />
    );
  }
  const clamp = (candidate: number) => Math.min(max, Math.max(min, Math.round(candidate)));
  const current = value.trim() === '' || Number.isNaN(Number(value)) ? null : clamp(Number(value));
  const step = (delta: number) => {
    onChange(String(current === null ? (delta > 0 ? min : max) : clamp(current + delta)));
  };
  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      {presets.length === 0 ? null : (
        <div
          className={styles['presets']}
          role="group"
          aria-label={`Common ${label.toLowerCase()}`}
        >
          {presets.map((preset) => (
            <button
              key={preset}
              className={`${(current === preset ? buttons['primary'] : buttons['secondary']) ?? ''} ${buttons['small'] ?? ''}`}
              type="button"
              aria-pressed={current === preset}
              onClick={() => {
                onChange(String(preset));
              }}
            >
              {preset}
            </button>
          ))}
        </div>
      )}
      <div className={styles['stepper']}>
        <button
          className={`${buttons['secondary'] ?? ''} ${buttons['icon'] ?? ''}`}
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={current !== null && current <= min}
          onClick={() => {
            step(-1);
          }}
        >
          −
        </button>
        <input
          id={id}
          className={styles['input']}
          type="text"
          inputMode="numeric"
          value={value}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={describedBy(id, hint, error)}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onBlur={() => {
            if (current !== null) {
              onChange(String(current));
            }
          }}
        />
        <button
          className={`${buttons['secondary'] ?? ''} ${buttons['icon'] ?? ''}`}
          type="button"
          aria-label={`Increase ${label}`}
          disabled={current !== null && current >= max}
          onClick={() => {
            step(1);
          }}
        >
          +
        </button>
      </div>
    </FieldShell>
  );
}

interface CheckboxFieldProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}

export function CheckboxField({ label, checked, onChange }: CheckboxFieldProps) {
  const id = useId();
  return (
    <div className={styles['checkbox']}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

interface SelectFieldProps extends BaseProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
}

export function SelectField({ label, value, onChange, options, error, hint }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      <select
        id={id}
        className={`${styles['input'] ?? ''} ${styles['select'] ?? ''}`}
        value={value}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

interface FormActionsProps {
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly disabled?: boolean;
  readonly onCancel?: (() => void) | undefined;
  readonly error?: string | undefined;
}

export function FormActions({
  submitLabel,
  pending,
  disabled = false,
  onCancel,
  error,
}: FormActionsProps) {
  return (
    <div className={styles['actions']}>
      {error === undefined ? null : (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      )}
      <div className={styles['buttons']}>
        <button className={styles['primary']} type="submit" disabled={pending || disabled}>
          {pending ? 'Saving…' : submitLabel}
        </button>
        {onCancel === undefined ? null : (
          <button className={buttons['secondary']} type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
